import React, { useState } from 'react';
import { useT } from './BuilderApp.jsx';
import { ChevronLeft } from '../components/ui/Icons.jsx';
import { PACKAGES, PKG_TEXT, addonTotalFor } from '../data/packages.js';
import { pkgById } from '../data/defaults.js';
import SalesDialog from '../components/sections/SalesDialog.jsx';

export default function PaymentScreen({ config, lang, onBack, onPay }) {
  const t = useT();
  const [showRestricted, setShowRestricted] = useState(false);

  const pkg     = pkgById(config.package);
  const pkgIdx  = PACKAGES.findIndex(p => p.id === pkg.id);
  const pd      = t.pkgs[pkgIdx];
  const isStarter = pkg.id === 'starter';
  const no      = ['no', 'sv', 'da'].includes(lang);

  const selAddons = config.addons || [];
  const addonTotal = addonTotalFor(selAddons, lang);
  const useNOK  = ['no', 'da', 'sv'].includes(lang);
  const fmt     = n => useNOK ? `${n} kr${t.addons_mth}` : `€${n}${t.addons_mth}`;

  // Pro / Enterprise — show inline SalesDialog
  if (!isStarter) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem', position: 'relative', zIndex: 1 }}>
        <div style={{ width: '100%', maxWidth: 520 }}>
          <button className="btn btn-ghost btn-sm" onClick={onBack} style={{ gap: '.3rem', marginBottom: '1.5rem' }}>
            <ChevronLeft/> {t.back}
          </button>
          <SalesDialog pkgId={pkg.id} onClose={onBack} prefillEmail={config.owner_email || ''} lang={lang} inline={true}/>
        </div>
      </div>
    );
  }

  // Starter — free launch with dev placeholder
  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem', position: 'relative', zIndex: 1 }}>
      <div style={{ maxWidth: 460, width: '100%' }}>
        {/* <button className="btn btn-ghost btn-sm" onClick={onBack} style={{ gap: '.3rem', marginBottom: '1.75rem' }}>
          <ChevronLeft/> {t.back}
        </button> */}

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div style={{ fontSize: '1.5rem', marginBottom: '.5rem' }}>💳</div>
          <h2 style={{ fontSize: '1.45rem', fontWeight: 800, letterSpacing: '-.02em', marginBottom: '.3rem' }}>{t.pay_h}</h2>
          <p style={{ color: 'var(--muted)', fontSize: '.87rem' }}>{pkg.label} · <strong style={{ color: 'var(--text)' }}>{pd.price}</strong></p>
          {selAddons.length > 0 && (
            <p style={{ color: 'var(--muted)', fontSize: '.8rem', marginTop: '.2rem', marginBottom: 0 }}>
              {t.addons_total} <strong style={{ color: '#60a5fa' }}>{fmt(addonTotal)}</strong>
              {' · '}<span style={{ color: '#34d399', fontWeight: 700 }}>{t.addons_setup} {t.addons_free}</span>
            </p>
          )}
        </div>

        {/* Restricted industries notice */}
        <div style={{ marginBottom: '.9rem', borderRadius: 10, border: '1px solid rgba(251,191,36,.13)', overflow: 'hidden' }}>
          <button
            onClick={() => setShowRestricted(v => !v)}
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '.6rem', background: 'rgba(251,191,36,.04)', border: 'none', cursor: 'pointer', padding: '.55rem .75rem', textAlign: 'left' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '.45rem' }}>
              <span style={{ fontSize: '.85rem' }}>⚠️</span>
              <span style={{ fontSize: '.72rem', fontWeight: 700, color: 'rgba(251,191,36,.85)', letterSpacing: '.01em' }}>{t.restricted_h}</span>
            </div>
            <span style={{ fontSize: '.65rem', color: 'rgba(251,191,36,.55)', flexShrink: 0, fontWeight: 600 }}>
              {showRestricted ? t.restricted_toggle_open : t.restricted_toggle}
            </span>
          </button>
          {showRestricted && (
            <div style={{ padding: '.6rem .75rem', borderTop: '1px solid rgba(251,191,36,.1)', background: 'rgba(0,0,0,.2)' }}>
              <p style={{ fontSize: '.7rem', color: 'rgba(148,163,184,.7)', lineHeight: 1.65, margin: 0 }}>{t.restricted_body}</p>
            </div>
          )}
        </div>

        {/* SCA / BankID notice */}
        <div style={{ background: 'rgba(59,130,246,.06)', border: '1px solid rgba(59,130,246,.18)', borderRadius: 12, padding: '.75rem 1rem', marginBottom: '1.25rem', display: 'flex', gap: '.65rem', alignItems: 'flex-start' }}>
          <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>🔐</span>
          <p style={{ fontSize: '.77rem', color: 'rgba(148,163,184,.9)', lineHeight: 1.55, margin: 0 }}>
            {no
              ? 'Betaling bekreftes via BankID eller tilsvarende sterk kundeautentisering (SCA). Kortdata lagres aldri hos Chamcore.'
              : 'Payment is verified via BankID or equivalent Strong Customer Authentication (SCA). Card data is never stored by Chamcore.'}
          </p>
        </div>

        {/* Payment card — dev placeholder (no Stripe) */}
        <div className="glass" style={{ borderRadius: 16, padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
          <div style={{ border: '1px dashed rgba(251,191,36,.35)', borderRadius: 12, padding: '1.1rem 1rem', background: 'rgba(251,191,36,.04)', display: 'flex', gap: '.65rem', alignItems: 'flex-start' }}>
            <span style={{ fontSize: '1rem', flexShrink: 0 }}>🧪</span>
            <div>
              <div style={{ fontSize: '.75rem', fontWeight: 800, color: '#fbbf24', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: '.25rem' }}>FREE LAUNCH</div>
              <p style={{ fontSize: '.78rem', color: 'rgba(148,163,184,.8)', margin: 0, lineHeight: 1.5 }}>{t.pay_dev_note}</p>
            </div>
          </div>

          <button className="btn btn-primary btn-lg btn-full" onClick={onPay} style={{ marginTop: '.25rem' }}>
            {t.pay_btn(pd.price)}
          </button>

          <div style={{ textAlign: 'center', fontSize: '.71rem', color: 'var(--dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '.4rem' }}>
            <span>🔒</span> {t.pay_ssl}
          </div>
        </div>
      </div>
    </div>
  );
}
